import { Fragment, memo, useMemo } from 'react';
import tw from 'twin.macro';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core';
import FormControl from '@material-ui/core/FormControl';
import FormGroup from '@material-ui/core/FormGroup';
import { useMediaQuery } from 'react-responsive';
import clsx from 'clsx';
import _ from 'lodash';
import Dropdown from '../../Dropdown';
import Filter from '../Filter';
import Checkbox from '../../Checkbox';

const useFormStyle = makeStyles(() => ({
  root: {
    '&': tw`flex-nowrap pl-4 w-full`,
  },
}));

const CheckboxSelectFilter = ({
  options,
  values,
  onChange,
  label,
  variant,
  filterMaxWidthClass,
  filterSpacingClass,
  children,
  widthInside,
  menuWidthClass,
  selectedValuePrefix,
  childComponent,
  selectedValueLabel,
  bgClassName,
  checkboxStyles,
  checkboxGroupClasses,
  disabled,
  topComponent,
  onClose,
}) => {
  const formClasses = useFormStyle();

  const isEmpty = useMemo(() => {
    if (!values || !values.length || !options || !options.length) return true;
    // to check empty value, consider values which are in options.
    // For some agencies, we have default values which are not in options
    const filteredValues = _.filter(values, (v) => !!_.find(options, (o) => o.value === v));
    return !filteredValues.length;
  }, [options, values]);

  const getLabel = () => {
    if (isEmpty) {
      return label;
    }

    if (selectedValueLabel) {
      return selectedValueLabel;
    }

    const selectedOptions = options.filter((o) => values.includes(o.value));

    if (selectedOptions.length === 0) {
      return label;
    }

    const optionTexts = selectedOptions.map((o) =>
      typeof o.label === 'string' ? o.label : o.plainTextLabel || '',
    );

    if (optionTexts.every((t) => typeof t === 'string' && t.length > 0)) {
      return `${selectedValuePrefix} ${optionTexts.join(', ')}`.trim();
    }

    return (
      <span className="flex items-center flex-nowrap">
        {selectedValuePrefix}
        {selectedOptions.map((o, index) => (
          <span key={o.value} className="ml-1">
            {o.label}
            {index < selectedOptions.length - 1 && ','}
          </span>
        ))}
      </span>
    );
  };

  const handleClear = () => {
    // keep only the values that are not in the options
    const filteredValues =
      values && values.length
        ? _.filter(values, (v) => !_.find(options, (o) => o.value === v))
        : [];
    onChange(filteredValues);
  };

  const handleChange = (itemValue, newValue) => {
    let newValues = [...values];
    if (newValue && !newValues.includes(itemValue)) {
      newValues.push(itemValue);
    } else if (!newValue) {
      newValues = newValues.filter((v) => v !== itemValue);
    }
    onChange(newValues);
  };
  const isSmallScreen = useMediaQuery({ query: '(max-width: 768px)' });

  const tooltip = useMemo(() => {
    if (isEmpty) {
      return label;
    }
    const selectedOptions = options.filter((o) => values.includes(o.value));
    if (selectedOptions.length === 0) {
      return label;
    }
    const labels = selectedOptions.map(
      (o) => o.plainTextLabel || (typeof o.label === 'string' ? o.label : ''),
    );
    return `${selectedValuePrefix} ${labels.filter(Boolean).join(', ')}`.trim();
  }, [isEmpty, label, options, values, selectedValuePrefix]);

  return (
    <Filter
      label={getLabel()}
      tooltipText={tooltip}
      menuWidthClass={menuWidthClass}
      variant={variant}
      filterMaxWidthClass={filterMaxWidthClass}
      filterSpacingClass={filterSpacingClass}
      isFilterEmpty={isEmpty}
      handleClear={handleClear}
      widthInside={widthInside}
      bgClassName={bgClassName}
      dropdownAligning={isSmallScreen ? 'right' : 'left'}
      disabled={disabled}
      onClose={onClose}
    >
      <Dropdown.Item>
        {topComponent}
        <FormControl
          className="grid col-span-5 grid-cols-2 gap-y-2 w-full items-center"
          component="fieldset"
        >
          <FormGroup
            classes={formClasses}
            className={clsx('overflow-x-auto max-h-1', checkboxGroupClasses)}
          >
            {options.map((item) => (
              <Fragment key={item.value}>
                <Checkbox
                  checked={values.includes(item.value)}
                  name={item.label}
                  onChange={(event, newValue) => handleChange(item.value, newValue)}
                  value={item.value}
                  label={item.label}
                  key={item.value}
                  checkboxStyles={checkboxStyles}
                />
                {values.includes(item.value) && item.inputElement ? item.inputElement : ''}
              </Fragment>
            ))}
            {children}
          </FormGroup>
        </FormControl>
        {childComponent}
      </Dropdown.Item>
    </Filter>
  );
};

CheckboxSelectFilter.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.bool]),
      plainTextLabel: PropTypes.string,
      inputElement: PropTypes.node,
    }),
  ).isRequired,
  values: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])).isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  variant: PropTypes.string,
  filterMaxWidthClass: PropTypes.string,
  filterSpacingClass: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  widthInside: PropTypes.string,
  menuWidthClass: PropTypes.string,
  selectedValuePrefix: PropTypes.string,
  childComponent: PropTypes.node,
  selectedValueLabel: PropTypes.string,
  bgClassName: PropTypes.string,
  checkboxGroupClasses: PropTypes.string,
  checkboxStyles: PropTypes.shape(),
  disabled: PropTypes.bool,
  topComponent: PropTypes.node,
  onClose: PropTypes.func,
};

CheckboxSelectFilter.defaultProps = {
  variant: 'standard',
  filterMaxWidthClass: 'max-w-filter',
  // Undefined keeps Filter's own spacing for every existing caller; the filter bar passes its own so
  // all of its pills come out the same height.
  filterSpacingClass: undefined,
  children: null,
  widthInside: '',
  menuWidthClass: 'w-64',
  selectedValuePrefix: '',
  childComponent: undefined,
  selectedValueLabel: undefined,
  bgClassName: undefined,
  checkboxStyles: undefined,
  checkboxGroupClasses: undefined,
  disabled: false,
  topComponent: undefined,
  onClose: undefined,
};

export default memo(CheckboxSelectFilter);
